import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useJsonToCsv } from 'react-json-csv';
import {
  Button,
  Table,
  Tabs,
  Image,
  Input,
  Avatar,
  notification,
  Tooltip,
  Select,
  Popconfirm,
  DatePicker,
  Card
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { useRecoilValue, useSetRecoilState, useRecoilState, useRecoilValueLoadable } from 'recoil';
import moment from 'moment';
import {
  businessAtom,
  showCreateSharedStoreAtom,
  reloadSharedStoresAtom,
  selectedInventoryItemAtom,
  timezoneAtom,
  profileAtom
} from '../../../atoms/Atoms';
import api from '../../../api/api';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';
import AssignSharedStoreModal from './shared-components/AssignSharedStoreModal';
import ItemHistoryModal from './shared-components/ItemHistoryModal';
import { mapInventoryReasonToString, mapInventoryTypeToString, renderDate, thousand, toCurrency } from '../../utils/functions';
import envConfig from '../../../envConfig';
import { dateRangePresets } from '../../utils/utils';

function Inventory() {
  const bottomRowGridStyle = { width: '100%', textAlign: 'center' };
  const { saveAsCsv } = useJsonToCsv();
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const setShowCreateSharedStore = useSetRecoilState(showCreateSharedStoreAtom);
  const [reloadSharedStores, setReloadSharedStores] = useRecoilState(reloadSharedStoresAtom);
  const [selectedInventoryItem, setSelectedInventoryItem]
    = useRecoilState(selectedInventoryItemAtom);
  const business = useRecoilValue(businessAtom);
  const [isSearchingInventory, setIsSearchingInventory] = useState(false);
  const [isSearchingReport, setIsSearchingReport] = useState(false);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [lastSearchInventory, setLastSearchInventory] = useState('');
  const [lastSearchReport, setLastSearchReport] = useState('');
  const [currentPageInventory, setCurrentPageInventory] = useState(0);
  const [currentPageReport, setCurrentPageReport] = useState(0);
  const [sizeInventory, setSizeInventory] = useState(20);
  const [sizeReport, setSizeReport] = useState(20);
  const [sorting, setSorting] = useState(0);
  const [productsInventory, setProductsInventory] = useState([]);
  const [movementReport, setMovementReport] = useState({});
  const [totalItemsInSearchInventory, setTotalItemsInSearchInventory] = useState(0);
  const [totalItemsInSearchReport, setTotalItemsInSearchReport] = useState(0);
  const [reportTypeFilter, setReportTypeFilter] = useState('');
  const [selectedReasons, setSelectedReasons] = useState(['']);
  const [allCategories, setAllCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  const [divisions, setDivisions] = useState([]);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [selectedDivisions, setSelectedDivisions] = useState([]);

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [sharedStores, setSharedStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportingCatalog, setExportingCatalog] = useState(false);
  const [selectedTab, setSelectedTab] = useState('0');
  const [shouldReset, setShouldReset] = useState({
    inventory: false,
    shared: false,
    report: false,
  });
  const [reportDateRange, setReportDateRange] = useState({
    start: moment().startOf('day').format('YYYY-MM-DD'),
    end: moment().endOf('day').format('YYYY-MM-DD'),
  });
  const timezone = useRecoilValue(timezoneAtom);
  const [loadingDataForExport, setLoadingDataForExport] = useState(false);
  const profile = useRecoilValueLoadable(profileAtom);
  const isAdmin =
    profile.contents && (profile?.contents?.roles?.includes('SuperAdmin'));
  const isStoreAdmin =
    isAdmin || (profile?.contents && profile?.contents?.roles?.includes('StoreAdmin'));
  const isStoreManager = isStoreAdmin || profile?.contents?.roles?.includes('StoreManager');

  const exportFields = {
    gtin: 'GTIN',
    itemName: t('item'),
    createdAt: t('created_on'),
    typeString: t('type'),
    reasonString: t('reason'),
    amount: t('amount'),
    comments: t('comments'),
    registeredByCompleteName: t('registered_by'),
  };

  function exportCatalog() {
    let url = `inventory/export/${business.id}?`;
    if (selectedDivisions?.length > 0) {
      url += `&divisions=${selectedDivisions}`;
    }
    if (selectedGroups?.length > 0) {
      url += `&groups=${selectedGroups}`;
    }
    if (selectedDepartments?.length > 0) {
      url += `&departmentIds=${selectedDepartments}`;
    }
    if (selectedCategories?.length > 0) {
      url += `&categoryIds=${selectedCategories}`;
    }
    setExportingCatalog(true);
    api
      .get(url, { responseType: 'blob' })
      .then((response) => {
        setExportingCatalog(false);
        const href = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `${business.name}_catalog_${new Date().toLocaleDateString()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        console.error(error);
        setExportingCatalog(false);
      });
  }

  function mapExportReport(tempMovement) {
    return _.map(tempMovement, (m) => ({
      gtin: m.businessItem.item.gtin,
      itemName: (i18n.language === 'en' ?
        (`${m.businessItem.item.brand} ${m.businessItem.item.name} ${m.businessItem.item.description}`).trim() :
        (`${m.businessItem.item.brand} ${m.businessItem.item.nameSpa} ${m.businessItem.item.descriptionSpa}`).trim())
        .replaceAll(',', ' '),
      createdAt: m.createdAt,
      typeString: mapInventoryTypeToString(t, m.type),
      reasonString: mapInventoryReasonToString(t, m.reason),
      amount: m.amount,
      comments: m.comments?.replaceAll(',', ' '),
      registeredByCompleteName: m?.registeredBy?.completeName?.replaceAll(',', ' ')
    }));
  }

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function searchProducts(query, page) {
    setIsSearchingInventory(true);
    const payload = {
      Query: query,
      Page: page,
      Size: sizeInventory,
      Sorting: sorting,
      BusinessId: business.id,
      Divisions: selectedDivisions,
      Groups: selectedGroups,
      SelectedCategoriesId: selectedCategories,
      SelectedDepartmentsId: selectedDepartments
    };
    api
      .post(
        'inventory/search',
        payload
      )
      .then((response) => {
        if (response.data.success) {
          setIsSearchingInventory(false);
          setTotalItemsInSearchInventory(response.data.data.total);
          setProductsInventory(response.data.data.result);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setIsSearchingInventory(false);
        showMessage(error);
      });
  }

  function mapReportMovement(tempMovement) {
    return _.map(tempMovement, (m) => ({
      ...m,
      gtin: m.businessItem.item.gtin,
      itemName: i18n.language === 'en' ?
        (`${m.businessItem.item.brand} ${m.businessItem.item.name} ${m.businessItem.item.description}`).trim() :
        (`${m.businessItem.item.brand} ${m.businessItem.item.nameSpa} ${m.businessItem.item.descriptionSpa}`).trim(),
      typeString: mapInventoryTypeToString(t, m.type),
      reasonString: mapInventoryReasonToString(t, m.reason),
      registeredByCompleteName: m?.registeredBy?.completeName
    }));
  }

  function searchReport(query, page, reasons) {
    setIsSearchingReport(true);
    let tempType = null;
    if (reportTypeFilter !== '') {
      tempType = parseInt(reportTypeFilter, 10);
    }
    const tempReasons = [];
    for (let i = 0; i < reasons.length; i++) {
      if (reasons[i] !== '') {
        tempReasons.push(parseInt(reasons[i], 10));
      }
    }
    const payload = {
      Query: query,
      Page: page,
      Size: sizeReport,
      Type: tempType,
      Reasons: tempReasons,
      BusinessId: business?.id,
      StartDate: dayjs(reportDateRange.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
      EndDate: dayjs(reportDateRange.end).endOf('day').add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
    };
    api
      .post(
        'inventory/movement/report',
        payload
      )
      .then((response) => {
        setIsSearchingReport(false);
        if (response.data.success) {
          setTotalItemsInSearchReport(response.data.data.total);
          setMovementReport({
            ...response.data.data,
            movement: mapReportMovement(response.data.data.movement)
          });
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setIsSearchingReport(false);
        showMessage(error.message);
      });
  }

  function getExportData() {
    setLoadingDataForExport(true);
    let tempType = null;
    if (reportTypeFilter !== '') {
      tempType = parseInt(reportTypeFilter, 10);
    }
    const tempReasons = [];
    for (let i = 0; i < selectedReasons.length; i++) {
      if (selectedReasons[i] !== '') {
        tempReasons.push(parseInt(selectedReasons[i], 10));
      }
    }
    const payload = {
      Query: lastSearchReport,
      Type: tempType,
      Reasons: tempReasons,
      BusinessId: business?.id,
      StartDate: dayjs(reportDateRange.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
      EndDate: dayjs(reportDateRange.end).endOf('day').add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
    };
    api
      .post(
        'inventory/movement/report/all',
        payload
      )
      .then((response) => {
        setLoadingDataForExport(false);
        if (response.data.success) {
          const data = mapExportReport(response.data.data);
          saveAsCsv({
            data,
            fields: exportFields,
            filename: `inventory_movement_${reportDateRange.start}_${reportDateRange.end}`,
          });
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setLoadingDataForExport(false);
        showMessage(error.message);
      });
  }

  function getDivisions() {
    setLoadingDivisions(true);
    api.get('businesses/divisions')
      .then((response) => {
        setLoadingDivisions(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          setDivisions(_.filter(response.data.data.results, (d) => d !== null && d.trim() !== ''));
        }
      })
      .catch((err) => {
        setLoadingDivisions(false);
        showMessage(err.message);
      });
  }

  function getGroups() {
    setLoadingGroups(true);
    api.get(`businesses/${business?.id}/groups`)
      .then((response) => {
        setLoadingGroups(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          setGroups(_.filter(response.data.data.results, (d) => d !== null && d.trim() !== ''));
        }
      })
      .catch((err) => {
        setLoadingGroups(false);
        showMessage(err.message);
      });
  }

  function getDepartments() {
    setLoadingDepartments(true);
    api.get('businesses/supersections')
      .then((response) => {
        setLoadingDepartments(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          setDepartments(response.data.data.results);
        }
      })
      .catch((err) => {
        setLoadingDepartments(false);
        showMessage(err.message);
      });
  }

  function getCategories() {
    setLoadingCategories(true);
    api.get('businesses/menusections')
      .then((response) => {
        setLoadingCategories(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          setAllCategories(response.data.data.results);
        }
      })
      .catch((err) => {
        setLoadingCategories(false);
        showMessage(err.message);
      });
  }

  function onSearch(value) {
    setLastSearchInventory(value);
    setCurrentPageInventory(0);
    searchProducts(value, 0);
  }

  function reportSearch(value, reasons) {
    setSelectedReasons(reasons);
    setLastSearchReport(value);
    setCurrentPageReport(0);
    searchReport(value, 0, reasons);
  }

  function onInventoryTableChange(page, pageSize) {
    if (page - 1 !== currentPageInventory) {
      setCurrentPageInventory(page - 1);
      searchProducts(lastSearchInventory, page - 1);
    } else {
      setSizeInventory(pageSize);
    }
  }

  function onReportTableChange(page, pageSize) {
    if (page - 1 !== currentPageReport) {
      setCurrentPageReport(page - 1);
      searchReport(lastSearchReport, page - 1, selectedReasons);
    } else {
      setSizeReport(pageSize);
    }
  }

  function getSharedStores() {
    setIsLoadingStores(true);
    if (reloadSharedStores) {
      setReloadSharedStores(false);
    }
    api
      .get(`inventory/shared/${business?.id}`)
      .then((response) => {
        setIsLoadingStores(false);
        if (response.data.success) {
          setSharedStores(response.data.data.results);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setIsLoadingStores(false);
        showMessage(error);
      });
  }

  function copyInventory(fromStoreId) {
    setLoading(true);
    api
      .post(`inventory/shared/copy/${fromStoreId}/${business?.id}`, null)
      .then((response) => {
        setLoading(false);
        if (!response.data.success) {
          showMessage(response.data.error);
        } else {
          showMessage(t('copy_inventory_successful'));
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  }

  function removeSharedStore(storeId) {
    setLoading(true);
    api
      .post('inventory/shared/remove', {
        BusinessId: business?.id,
        SharedBusinessId: storeId
      })
      .then((response) => {
        setLoading(false);
        if (!response.data.success) {
          showMessage(response.data.error);
        } else {
          showMessage(t('store_removed_successfully'));
          setSharedStores(response.data.data.results);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  }

  function revert(movementId) {
    setLoading(true);
    api
      .post(`inventory/revert/${movementId}`, null)
      .then((response) => {
        setLoading(false);
        if (!response.data.success) {
          showMessage(response.data.error);
        } else {
          reportSearch(lastSearchReport, selectedReasons);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  }

  function calculatePacks(item) {
    let tempUnitsPerPackage = item.unitsPerPackage;
    if (tempUnitsPerPackage === 0) {
      tempUnitsPerPackage = 1;
    }
    const temp = item.quantityOnHand / tempUnitsPerPackage;
    if (Math.floor(temp) < temp) {
      const individuals = item.quantityOnHand - (Math.floor(temp) * item.unitsPerPackage);
      return `${thousand(Math.floor(temp))} ${t('packs')}, ${thousand(individuals)} ${t('units')}`;
    }
    return `${thousand(temp)} ${t('packs')}`;
  }

  const reportColumns = [
    {
      title: 'GTIN',
      dataIndex: 'gtin',
      key: 'gtin',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{text}</span>,
    },
    {
      title: t('item'),
      dataIndex: 'itemName',
      key: 'itemName',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{text}</span>,
    },
    {
      title: t('created_on'),
      dataIndex: 'createdAt',
      key: 'name',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{renderDate(text, timezone)}</span>,
    },
    {
      title: t('type'),
      dataIndex: 'typeString',
      key: 'type',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{text}</span>,
    },
    {
      title: t('reason'),
      dataIndex: 'reasonString',
      key: 'reason',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{text}</span>,
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{thousand(text)}</span>,
    },
    {
      title: t('comments'),
      dataIndex: 'comments',
      key: 'comments',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{text}</span>,
    },
    {
      title: t('registered_by'),
      key: 'registered_by',
      align: 'left',
      className: 'text-xs',
      render: (text) =>
        <span>{text.registeredBy?.completeName}</span>,
    },
    {
      title: t('actions'),
      key: 'manage',
      align: 'center',
      width: 30,
      className: 'text-xs',
      render: (text, item) => (
        <Button
          size="small"
          type="primary"
          danger
          disabled={!isStoreManager}
          onClick={() => revert(item.id)}
        >
          {t('remove')}
        </Button>
      ),
    },
  ];

  if (!business) {
    history.push('/');
    return null;
  }

  useEffect(() => {
    onSearch('');
    getSharedStores();
    getDivisions();
    getGroups();
    getDepartments();
    getCategories();
    setSelectedDivisions([]);
    setSelectedGroups([]);
    setSelectedDepartments([]);
    setSelectedCategories([]);
  }, []);

  useEffect(() => {
    if (selectedTab === '0') {
      onSearch('');
    } else {
      setShouldReset({
        ...shouldReset,
        shared: true,
        report: true
      });
    }
    if (selectedTab === '1') {
      getSharedStores();
    } else {
      setShouldReset({
        ...shouldReset,
        inventory: true,
        report: true
      });
    }
    if (selectedTab === '2') {
      reportSearch('', selectedReasons);
    } else {
      setShouldReset({
        ...shouldReset,
        inventory: true,
        shared: true
      });
    }
  }, [business]);

  useEffect(() => {
    if (reloadSharedStores) {
      getSharedStores();
    }
  }, [reloadSharedStores]);

  useEffect(() => {
    onSearch(lastSearchInventory);
  }, [sorting]);

  useEffect(() => {
    if (!selectedInventoryItem) {
      searchProducts(lastSearchInventory, currentPageInventory);
    }
  }, [selectedInventoryItem]);

  useEffect(() => {
    if (shouldReset.inventory && selectedTab === '0') {
      onSearch('');
      setShouldReset({
        ...shouldReset,
        inventory: false
      });
    }
    if (shouldReset.shared && selectedTab === '1') {
      getSharedStores();
      setShouldReset({
        ...shouldReset,
        shared: false
      });
    }
    if (shouldReset.report && selectedTab === '2') {
      reportSearch('', selectedReasons);
      setShouldReset({
        ...shouldReset,
        report: false
      });
    }
  }, [selectedTab]);

  useEffect(() => {
    reportSearch(lastSearchReport, selectedReasons);
  }, [reportDateRange]);

  useEffect(() => {
    reportSearch(lastSearchReport, ['']);
  }, [reportTypeFilter]);

  useEffect(() => {
    if (selectedDepartments?.length > 0) {
      setCategories(_.filter(allCategories, (c) => selectedDepartments.includes(c.superSectionId)));
    } else {
      setCategories(allCategories);
    }
    setSelectedCategories([]);
  }, [selectedDepartments]);

  useEffect(() => {
    if (selectedDepartments?.length > 0) {
      setCategories(_.filter(allCategories, (c) => selectedDepartments.includes(c.superSectionId)));
    } else {
      setCategories(allCategories);
    }
  }, [allCategories]);

  return (
    <div>
      <PageHeader
        className="px-0"
        title={t('inventory')}
      />
      <Tabs onChange={(key) => setSelectedTab(key)} activeKey={selectedTab}>
        <Tabs.TabPane tab={t('items')} key="0">
          <div className="space-y-2">
            <div className="flex">
              <Button
                className="ml-auto"
                type="primary"
                loading={exportingCatalog}
                onClick={() => exportCatalog()}
              >
                {t('export_catalog')}
              </Button>
            </div>
            <div className="flex">
              <Input.Search
                key="itemSearch"
                placeholder={t('item_search_placeholder')}
                allowClear
                enterButton={t('search')}
                disabled={isSearchingInventory}
                size="large"
                onSearch={onSearch}
              />
              <Select
                className="mt-1 ml-2 w-48"
                mode="multiple"
                placeholder={t('any_division')}
                disabled={isSearchingInventory || divisions?.length < 2}
                loading={loadingDivisions}
                onChange={(value) => setSelectedDivisions(value)}
                value={selectedDivisions}
              >
                {/* <Option value="">{t('any_division')}</Option> */}
                {_.map(divisions, (c) => (
                  <Option value={c}>{c}</Option>
                ))}
              </Select>
              <Select
                className="mt-1 ml-2 w-48"
                mode="multiple"
                placeholder={t('any_group')}
                disabled={isSearchingInventory || groups?.length < 2}
                loading={loadingGroups}
                onChange={(value) => setSelectedGroups(value)}
                value={selectedGroups}
              >
                {/* <Option value="">{t('any_group')}</Option> */}
                {_.map(groups, (c) => (
                  <Option value={c}>{c}</Option>
                ))}
              </Select>
              <Select
                className="mt-1 ml-2 w-48"
                mode="multiple"
                placeholder={t('any_departmnt')}
                disabled={isSearchingInventory || departments?.length < 2}
                loading={loadingDepartments}
                onChange={(value) => setSelectedDepartments(value)}
                value={selectedDepartments}
              >
                {/* <Option value="">{t('any_departmnt')}</Option> */}
                {_.map(departments, (c) => (
                  <Option value={c.id}>{i18n.language === 'en' ? c.nameEng : c.name}</Option>
                ))}
              </Select>
              <Select
                className="mt-1 ml-2 w-48"
                mode="multiple"
                placeholder={t('any_category')}
                disabld={isSearchingInventory || categories?.length < 2}
                loading={loadingCategories}
                onChange={(value) => setSelectedCategories(value)}
                value={selectedCategories}
              >
                {/* <Option value="">{t('any_category')}</Option> */}
                {_.map(categories, (c) => (
                  <Option value={c.id}>{i18n.language === 'en' ? c.nameEng : c.name}</Option>
                ))}
              </Select>
              <Select
                className="mt-1 ml-2"
                style={{ width: 150 }}
                disabled={isSearchingInventory}
                onChange={(value) => setSorting(value)}
                value={sorting}
              >
                <Option value={0}>{t('default_sort')}</Option>
                <Option value={1}>ASC</Option>
                <Option value={2}>DESC</Option>
              </Select>
            </div>
            <Table
              size="small"
              loading={isSearchingInventory}
              bordered
              pagination={{
                pageSize: sizeInventory,
                showSizeChanger: true,
                defaultCurrent: 0,
                current: currentPageInventory + 1,
                total: totalItemsInSearchInventory,
                onChange: onInventoryTableChange
              }}
              columns={[
                {
                  title: 'UPC',
                  key: 'upc',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{text.upc}</span>,
                },
                {
                  title: t('image'),
                  key: 'image',
                  align: 'center',
                  width: 80,
                  className: 'text-xs',
                  render: (text) =>
                    <Image
                      width={50}
                      src={text.itemImage}
                      alt={`${text.brand} ${text.name} ${text.description}`}
                    />,
                },
                {
                  title: t('brand'),
                  key: 'brand',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{text.brand}</span>,
                },
                {
                  title: t('name'),
                  key: 'name',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{text.name}</span>,
                },
                {
                  title: t('description'),
                  key: 'description',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{text.description}</span>,
                },
                {
                  title: t('cost'),
                  key: 'cost',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{toCurrency(text.cost) === '-' ? '$0.00' : toCurrency(text.cost)}</span>,
                },
                {
                  title: t('quantity_on_hand'),
                  key: 'quantityOnHand',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{thousand(text.quantityOnHand)}</span>,
                },
                {
                  title: t('pack'),
                  key: 'pack',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{thousand(text.pack)}</span>,
                },
                {
                  title: t('packs'),
                  key: 'packs',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span className="lowercase">{calculatePacks(text)}</span>,
                },
                {
                  title: t('actions'),
                  key: 'manage',
                  align: 'center',
                  width: 30,
                  className: 'text-xs',
                  render: (text, item) => (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        setSelectedInventoryItem(item);
                      }}
                    >
                      {t('manage')}
                    </Button>
                  ),
                },
              ]}
              dataSource={productsInventory}
            />
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('shared_inventory')} key="1">
          <div>
            <Button
              className="float-right mb-2"
              size="small"
              type="primary"
              onClick={() => {
                setShowCreateSharedStore(true);
              }}
            >
              {t('add')}
            </Button>
            <Table
              size="small"
              className="mt-2"
              loading={isLoadingStores}
              bordered
              pagination="none"
              columns={[
                {
                  title: t('name'),
                  key: 'name',
                  align: 'left',
                  className: 'text-xs',
                  render: (text) =>
                    <span>{text.name}</span>,
                },
                {
                  title: t('actions'),
                  key: 'manage',
                  align: 'center',
                  width: 30,
                  className: 'text-xs',
                  render: (text, item) => (
                    <div className="flex">
                      <Popconfirm
                        key="copy_inv"
                        title={t('copy_inventory_prompt')}
                        okText={t('yes')}
                        onConfirm={() => {
                          copyInventory(item.id);
                        }}
                      >
                        <Button
                          className="mr-2"
                          loading={loading}
                          size="small"
                          type="primary"
                        >
                          {t('copy_inventory')}
                        </Button>
                      </Popconfirm>
                      <Popconfirm
                        key="remove"
                        title={t('remove_business')}
                        okText={t('yes')}
                        onConfirm={() => {
                          removeSharedStore(item.id);
                        }}
                      >
                        <Button
                          loading={loading}
                          size="small"
                          type="primary"
                          danger
                        >
                          {t('remove')}
                        </Button>
                      </Popconfirm>
                    </div>
                  ),
                },
              ]}
              dataSource={sharedStores}
            />
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('movement_history')} key="2">
          <div>
            <dl className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              <Card
                type="inner"
                title={t('sale')}
                className="rounded-xl bg-gradient-to-br from-blue-200 to-white"
                hoverable
                loading={loading}
              >
                <Card.Grid
                  className="bg-white"
                  hoverable={false}
                  style={bottomRowGridStyle}
                >
                  <p>{t('count')}: {thousand(movementReport?.totalSale)}</p>
                  <p>{t('amount')}: {thousand(movementReport?.saleAmount)}</p>
                </Card.Grid>
              </Card>
              <Card
                type="inner"
                title={t('receiving')}
                className="rounded-xl bg-gradient-to-br from-blue-200 to-white"
                hoverable
                loading={loading}
              >
                <Card.Grid
                  className="bg-white"
                  hoverable={false}
                  style={bottomRowGridStyle}
                >
                  <p>{t('count')}: {thousand(movementReport?.totalReceiving)}</p>
                  <p>{t('amount')}: {thousand(movementReport?.receivingAmount)}</p>
                </Card.Grid>
              </Card>
              <Card
                type="inner"
                title={t('adjustment_up')}
                className="rounded-xl bg-gradient-to-br from-blue-200 to-white"
                hoverable
                loading={loading}
              >
                <Card.Grid
                  className="bg-white"
                  hoverable={false}
                  style={bottomRowGridStyle}
                >
                  <p>{t('count')}: {thousand(movementReport?.totalAdjustmentUp)}</p>
                  <p>{t('amount')}: {thousand(movementReport?.adjustmentUpAmount)}</p>
                </Card.Grid>
              </Card>
              <Card
                type="inner"
                title={t('adjustment_down')}
                className="rounded-xl bg-gradient-to-br from-blue-200 to-white"
                hoverable
                loading={loading}
              >
                <Card.Grid
                  className="bg-white"
                  hoverable={false}
                  style={bottomRowGridStyle}
                >
                  <p>{t('count')}: {thousand(movementReport?.totalAdjustmentDown)}</p>
                  <p>{t('amount')}: {thousand(movementReport?.adjustmentDownAmount)}</p>
                </Card.Grid>
              </Card>
            </dl>
            <div className="flex my-2">
              <Button
                type="primary"
                size="small"
                className="ml-auto"
                disabled={isSearchingReport}
                loading={loadingDataForExport}
                icon={<CloudDownloadOutlined />}
                onClick={() => {
                  getExportData();
                }}
              >
                {t('export')}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:grid-cols-6">
              <Input.Search
                key="reportSearch"
                disabled={isSearchingReport || loadingDataForExport}
                placeholder={t('item_search_placeholder')}
                allowClear
                enterButton={t('search')}
                size="large"
                onSearch={(val) => reportSearch(val, selectedReasons)}
                className="col-span-1 md:col-span-2 lg:col-span-3"
              />
              <RangePicker
                className="col-span-1"
                format="YYYY-MM-DD"
                disabled={isSearchingReport || loadingDataForExport}
                value={[dayjs(reportDateRange?.start), dayjs(reportDateRange?.end)]}
                presets={dateRangePresets(t)}
                onChange={(date, dateString) => {
                  setReportDateRange({
                    start: dateString[0],
                    end: dateString[1],
                  });
                }}
              />
              <Select
                className="h-10 col-span-1"
                disabled={isSearchingReport || loadingDataForExport}
                onChange={(value) => setReportTypeFilter(value)}
                value={reportTypeFilter}
              >
                <Option value="">{t('all')}</Option>
                <Option value="0">{t('sale')}</Option>
                <Option value="1">{t('adjustment_up')}</Option>
                <Option value="2">{t('adjustment_down')}</Option>
                <Option value="3">{t('receiving')}</Option>
              </Select>
              <Select
                mode="multiple"
                value={selectedReasons}
                className="h-10 col-span-1"
                disabled={isSearchingReport || loadingDataForExport}
                onChange={(value) => {
                  if (!value || value?.length === 0) {
                    setSelectedReasons(['']);
                  } else if (value[value.length - 1] !== '') {
                    setSelectedReasons(_.filter(value, (r) => r !== ''));
                  } else {
                    setSelectedReasons(['']);
                  }
                }}
              >
                {reportTypeFilter === '' && (
                  <>
                    <Option value="">{t('all')}</Option>
                    <Option value={0}>{t('none')}</Option>
                    <Option value={1}>{t('damage')}</Option>
                    <Option value={2}>{t('lost')}</Option>
                    <Option value={3}>{t('found')}</Option>
                    <Option value={4}>{t('replenishment')}</Option>
                    <Option value={5}>{t('sampling')}</Option>
                    <Option value={6}>{t('gift')}</Option>
                    <Option value={7}>{t('spoiled')}</Option>
                    <Option value={8}>{t('transfer_to_kitchen')}</Option>
                    <Option value={9}>{t('transfer_to_other_store')}</Option>
                    <Option value={10}>{t('transfer_to_deli')}</Option>
                  </>
                )}
                {(reportTypeFilter === '0' || reportTypeFilter === '1' ||
                  reportTypeFilter === '3') && (
                    <>
                      <Option value="">{t('all')}</Option>
                      <Option value={0}>{t('none')}</Option>
                      <Option value={3}>{t('found')}</Option>
                      <Option value={4}>{t('replenishment')}</Option>
                    </>
                )}
                {(reportTypeFilter === '2') && (
                  <>
                    <Option value="">{t('all')}</Option>
                    <Option value={0}>{t('none')}</Option>
                    <Option value={1}>{t('damage')}</Option>
                    <Option value={2}>{t('lost')}</Option>
                    <Option value={5}>{t('sampling')}</Option>
                    <Option value={6}>{t('gift')}</Option>
                    <Option value={7}>{t('spoiled')}</Option>
                    <Option value={8}>{t('transfer_to_kitchen')}</Option>
                    <Option value={9}>{t('transfer_to_other_store')}</Option>
                    <Option value={10}>{t('transfer_to_deli')}</Option>
                  </>
                )}
              </Select>
              {/* <Select
                className="h-10 lg:w-56"
                disabled={isSearchingReport || loadingDataForExport}
                onChange={(value) => setSelectedReason(value)}
                value={selectedReason}
              >
                {reportTypeFilter === '' && (
                  <>
                    <Option value="">{t('all')}</Option>
                    <Option value={0}>{t('none')}</Option>
                    <Option value={1}>{t('damage')}</Option>
                    <Option value={2}>{t('lost')}</Option>
                    <Option value={3}>{t('found')}</Option>
                    <Option value={4}>{t('replenishment')}</Option>
                    <Option value={5}>{t('sampling')}</Option>
                    <Option value={6}>{t('gift')}</Option>
                    <Option value={7}>{t('spoiled')}</Option>
                    <Option value={8}>{t('transfer_to_kitchen')}</Option>
                    <Option value={9}>{t('transfer_to_other_store')}</Option>
                    <Option value={10}>{t('transfer_to_deli')}</Option>
                  </>
                )}
                {(reportTypeFilter === '0' || reportTypeFilter === '1' ||
                  reportTypeFilter === '3') && (
                    <>
                      <Option value="">{t('all')}</Option>
                      <Option value={0}>{t('none')}</Option>
                      <Option value={3}>{t('found')}</Option>
                      <Option value={4}>{t('replenishment')}</Option>
                    </>
                )}
                {(reportTypeFilter === '2') && (
                  <>
                    <Option value="">{t('all')}</Option>
                    <Option value={0}>{t('none')}</Option>
                    <Option value={1}>{t('damage')}</Option>
                    <Option value={2}>{t('lost')}</Option>
                    <Option value={5}>{t('sampling')}</Option>
                    <Option value={6}>{t('gift')}</Option>
                    <Option value={7}>{t('spoiled')}</Option>
                    <Option value={8}>{t('transfer_to_kitchen')}</Option>
                    <Option value={9}>{t('transfer_to_other_store')}</Option>
                    <Option value={10}>{t('transfer_to_deli')}</Option>
                  </>
                )}
              </Select> */}
            </div>
            <Table
              size="small"
              className="mt-2"
              loading={isSearchingReport}
              bordered
              pagination={{
                pageSize: sizeReport,
                showSizeChanger: true,
                defaultCurrent: 0,
                current: currentPageReport + 1,
                total: totalItemsInSearchReport,
                onChange: onReportTableChange
              }}
              dataSource={movementReport.movement}
              columns={reportColumns}
            />
          </div>
        </Tabs.TabPane>
      </Tabs>
      <AssignSharedStoreModal />
      <ItemHistoryModal />
    </div>
  );
}

export default Inventory;
